import * as React from "react";
import { cn } from "@/lib/utils";

interface DropdownMenuContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  contentId: string;
}
const DropdownMenuContext = React.createContext<DropdownMenuContextValue | null>(null);
function useDropdownMenu() {
  const context = React.useContext(DropdownMenuContext);
  if (!context) throw new Error("Dropdown menu parts must be inside DropdownMenu");
  return context;
}

function DropdownMenu({
  children,
  open: controlledOpen,
  onOpenChange,
}: {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const contentId = React.useId();
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (controlledOpen === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  return (
    <DropdownMenuContext.Provider value={{ open, setOpen, triggerRef, contentId }}>
      {children}
    </DropdownMenuContext.Provider>
  );
}

function DropdownMenuTrigger({
  className,
  onClick,
  onKeyDown,
  ...props
}: React.ComponentProps<"button">) {
  const { open, setOpen, triggerRef, contentId } = useDropdownMenu();
  return (
    <button
      ref={triggerRef}
      type="button"
      aria-haspopup="menu"
      aria-expanded={open}
      aria-controls={open ? contentId : undefined}
      className={cn(
        "inline-flex items-center justify-center rounded-md outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
        className,
      )}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) {
          setOpen(!open);
          if (!open)
            requestAnimationFrame(() => {
              // Only take focus if nothing has claimed it since. A deferred focus that always
              // fires yanks it back from whatever the keyboard or pointer moved to first.
              const content = document.getElementById(contentId);
              if (!content || content.contains(document.activeElement)) return;
              content.querySelector<HTMLElement>("[role=menuitem]")?.focus();
            });
        }
      }}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (event.defaultPrevented) return;
        if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          setOpen(true);
          requestAnimationFrame(() =>
            document
              .getElementById(contentId)
              ?.querySelector<HTMLElement>("[role=menuitem]")
              ?.focus(),
          );
        }
      }}
      {...props}
    />
  );
}

function DropdownMenuContent({
  className,
  onKeyDown,
  align = "start",
  side = "bottom",
  ...props
}: React.ComponentProps<"div"> & {
  align?: "start" | "end";
  side?: "top" | "bottom" | "left" | "right";
}) {
  const { open, setOpen, triggerRef, contentId } = useDropdownMenu();
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (
        !ref.current?.contains(event.target as Node) &&
        !triggerRef.current?.contains(event.target as Node)
      )
        setOpen(false);
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onEscape);
    };
  }, [open, setOpen, triggerRef]);
  if (!open) return null;
  return (
    <div
      ref={ref}
      id={contentId}
      role="menu"
      data-side={side}
      data-align={align}
      className={cn(
        "absolute z-50 min-w-40 rounded-lg border bg-popover p-1 text-popover-foreground shadow-dialog",
        side === "top" ? "bottom-full mb-2" : side === "bottom" ? "top-full mt-2" : "top-0",
        side === "left"
          ? "right-full mr-2"
          : side === "right"
            ? "left-full ml-2"
            : align === "end" && "right-0",
        className,
      )}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (event.defaultPrevented) return;
        if (event.key === "Tab") {
          // Tab moves focus out of the menu by itself; what it does not do is close it, which
          // would leave the menu open over the page with focus somewhere else. Close once focus
          // has actually left, so tabbing within the controls (the appearance radios) keeps the
          // menu open.
          requestAnimationFrame(() => {
            if (!ref.current?.contains(document.activeElement)) setOpen(false);
          });
          return;
        }
        const items = [
          ...(ref.current?.querySelectorAll<HTMLElement>("[role=menuitem], [role=menuitemradio]") ??
            []),
        ];
        const index = items.indexOf(document.activeElement as HTMLElement);
        const next =
          event.key === "ArrowDown"
            ? items[(index + 1) % items.length]
            : event.key === "ArrowUp"
              ? items[(index - 1 + items.length) % items.length]
              : event.key === "Home"
                ? items[0]
                : event.key === "End"
                  ? items[items.length - 1]
                  : null;
        if (next) {
          event.preventDefault();
          next.focus();
        }
      }}
      {...props}
    />
  );
}

function DropdownMenuItem({ className, onClick, ...props }: React.ComponentProps<"button">) {
  const { setOpen, triggerRef } = useDropdownMenu();
  return (
    <button
      type="button"
      role="menuitem"
      className={cn(
        "flex h-11 w-full items-center gap-2 rounded-md px-2 text-left text-sm outline-none hover:bg-accent focus-visible:bg-accent disabled:cursor-not-allowed disabled:text-muted-foreground md:h-9",
        className,
      )}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) {
          setOpen(false);
          triggerRef.current?.focus();
        }
      }}
      {...props}
    />
  );
}

function DropdownMenuSeparator({ className, ...props }: React.ComponentProps<"div">) {
  return <div role="separator" className={cn("-mx-1 my-1 h-px bg-border", className)} {...props} />;
}

function DropdownMenuLabel({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn("px-2 py-1.5 text-xs font-medium text-muted-foreground", className)}
      {...props}
    />
  );
}

export {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
};
